import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ConfirmationDialog Component
 * Displays a confirmation dialog with customizable content and actions
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when the dialog is closed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message
 * @param {React.ReactNode} props.content - Custom dialog content (overrides message)
 * @param {string} props.confirmText - Text for confirm button
 * @param {string} props.cancelText - Text for cancel button
 * @param {string} props.confirmColor - Color for confirm button
 * @param {Function} props.onConfirm - Function to call when confirmed
 * @param {boolean} props.loading - Whether the dialog is in loading state
 * @param {boolean} props.fullWidth - Whether the dialog should take full width
 * @param {string} props.maxWidth - Maximum width of the dialog
 * @returns {React.ReactElement} Confirmation dialog component
 */
const ConfirmationDialog = ({
  open,
  onClose,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  content,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  onConfirm,
  loading = false,
  fullWidth = true,
  maxWidth = 'sm',
  height= '90%'
}) => {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby="confirmation-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '4px',
          margin: 'auto',
          maxHeight: '90vh',
          height: height,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Dialog Title */}
      <DialogTitle id="confirmation-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {!loading && (
            <IconButton edge="end" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      {/* Dialog Content */}
      <DialogContent>
        {content || (
          <DialogContentText>{message}</DialogContentText>
        )}
      </DialogContent>
      
      {/* Dialog Actions */}
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm} 
          color={confirmColor} 
          variant="contained" 
          disabled={loading}
          autoFocus
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;